import styled, { css } from 'styled-components';
import { Icon } from '../icon/index.js';
import { getTypographyStyles } from '../../utils/index.js';
import { SYS_SIZE_FIXED_50, SYS_SIZE_FIXED_1200, SYS_SIZE_FIXED_75, SYS_SIZE_FIXED_1400, SYS_SIZE_FIXED_25 } from '../../tokens/sizing.generated.js';
import { SYS_SPACE_FIXED_150, SYS_SPACE_FIXED_200, SYS_SPACE_FIXED_400, SYS_SPACE_FIXED_300, SYS_SPACE_FIXED_50 } from '../../tokens/spacing.generated.js';
import { SEM_OPACITY_ACTION_100_PCT, SEM_OPACITY_ACTION_20_PCT } from '../../tokens/opacity.generated.js';
import { SEM_TYPOGRAPHY_ACTION_MD_LABEL, SEM_TYPOGRAPHY_ACTION_SM_LABEL } from '../../tokens/typography.generated.js';
export const StyledLabel = styled.label `
  width: 100%;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.size === 'sm'
    ? `${SYS_SPACE_FIXED_150} ${SYS_SPACE_FIXED_300}`
    : `${SYS_SPACE_FIXED_200} ${SYS_SPACE_FIXED_400}`};
  gap: ${SYS_SPACE_FIXED_150};
  min-height: ${(props) => props.size === 'sm' ? SYS_SIZE_FIXED_1200 : SYS_SIZE_FIXED_1400};
  box-sizing: border-box;
  cursor: pointer;
  ${(props) => props.size === 'md'
    ? getTypographyStyles(SEM_TYPOGRAPHY_ACTION_MD_LABEL)
    : getTypographyStyles(SEM_TYPOGRAPHY_ACTION_SM_LABEL)}

  // TODO: missing token
  transition-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
  transition-duration: 250ms;
  transition-property: color, background-color, box-shadow;

  &::before {
    position: absolute;
    content: '';
    display: block;
    bottom: ${SYS_SPACE_FIXED_50};
    height: ${(props) => props.size == 'md' ? SYS_SIZE_FIXED_75 : SYS_SIZE_FIXED_50};
    width: calc(100% - 2 * ${SYS_SPACE_FIXED_50});
    // TODO: missing token
    transition-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
    transition-duration: 250ms;
    transition-property: opacity, background-color;
    opacity: ${SEM_OPACITY_ACTION_20_PCT};
  }
`;
export const StyledInput = styled.input `
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  width: 1px;
  position: absolute;
  opacity: 0;
  pointer-events: none;

  &:checked {
    & + ${StyledLabel} {
      &::before {
        opacity: ${SEM_OPACITY_ACTION_100_PCT};
      }
    }
  }

  &:focus {
    & + ${StyledLabel} {
      outline: 2px solid Highlight;
      outline: 2px solid -webkit-focus-ring-color;
      outline-style: auto;
    }
  }

  &:focus:not(:focus-visible) + ${StyledLabel} {
    outline: none;
  }
`;
export const StyledIcon = styled(Icon) `
  flex-shrink: 0;
`;
export const StyledContainer = styled.div `
  display: inline-flex;
  ${(props) => props.isFullWidth &&
    css `
      width: 100%;
    `}

  ${(props) => props.variant === 'primary' &&
    css `
      ${StyledLabel} {
        background: ${(props) => props.theme.sem.color.action.primary.surface.fill.default};
        color: ${(props) => props.theme.sem.color.action.primary.foreground.text.default};

        &::before {
          background-color: ${(props) => props.theme.sem.color.action.primary.foreground.icon.default};
        }
      }

      ${StyledInput}:disabled + ${StyledLabel} {
        cursor: not-allowed;
        background-color: ${(props) => props.theme.sem.color.action.primary.surface.fill.disabled};
        color: ${(props) => props.theme.sem.color.action.primary.foreground.text.disabled};

        &::before {
          background-color: ${(props) => props.theme.sem.color.action.primary.foreground.icon.disabled};
        }
      }

      &:hover {
        ${StyledInput}:not(:disabled) + ${StyledLabel} {
          background: ${(props) => props.theme.sem.color.action.primary.surface.fill.hover};
          color: ${(props) => props.theme.sem.color.action.primary.foreground.text.hover};

          &::before {
            background-color: ${(props) => props.theme.sem.color.action.primary.foreground.icon.hover};
          }
        }
      }

      &:active {
        ${StyledInput}:not(:disabled) + ${StyledLabel} {
          background: ${(props) => props.theme.sem.color.action.primary.surface.fill.pressed};
          color: ${(props) => props.theme.sem.color.action.primary.foreground.text.pressed};

          &::before {
            background-color: ${(props) => props.theme.sem.color.action.primary.foreground.icon.pressed};
          }
        }
      }
    `}

  ${(props) => props.variant === 'secondary' &&
    css `
      ${StyledLabel} {
        box-shadow: inset 0 0 0 1px
          ${(props) => props.theme.sem.color.action.secondary.surface.stroke.default};
        background: ${(props) => props.theme.sem.color.action.secondary.surface.fill.default};
        color: ${(props) => props.theme.sem.color.action.secondary.foreground.text.default};

        &::before {
          background-color: ${(props) => props.theme.sem.color.action.secondary.foreground.icon.default};
        }
      }

      ${StyledInput}:disabled + ${StyledLabel} {
        cursor: not-allowed;
        background-color: ${(props) => props.theme.sem.color.action.secondary.surface.fill.disabled};
        color: ${(props) => props.theme.sem.color.action.secondary.foreground.text.disabled};
        box-shadow: inset 0 0 0 1px
          ${(props) => props.theme.sem.color.action.secondary.surface.stroke.disabled};

        &::before {
          background-color: ${(props) => props.theme.sem.color.action.secondary.foreground.icon.disabled};
        }
      }

      &:hover {
        ${StyledInput}:not(:disabled) + ${StyledLabel} {
          background: ${(props) => props.theme.sem.color.action.secondary.surface.fill.hover};
          color: ${(props) => props.theme.sem.color.action.secondary.foreground.text.hover};
          box-shadow: inset 0 0 0 ${SYS_SIZE_FIXED_25}
            ${(props) => props.theme.sem.color.action.secondary.surface.stroke.hover};

          &::before {
            background-color: ${(props) => props.theme.sem.color.action.secondary.foreground.icon.hover};
          }
        }
      }

      &:active {
        ${StyledInput}:not(:disabled) + ${StyledLabel} {
          background: ${(props) => props.theme.sem.color.action.secondary.surface.fill.pressed};
          color: ${(props) => props.theme.sem.color.action.secondary.foreground.text.pressed};
          box-shadow: inset 0 0 0 ${SYS_SIZE_FIXED_25}
            ${(props) => props.theme.sem.color.action.secondary.surface.stroke.pressed};

          &::before {
            background-color: ${(props) => props.theme.sem.color.action.secondary.foreground.icon.pressed};
          }
        }
      }
    `}

    ${(props) => props.variant === 'tertiary' &&
    css `
      ${StyledLabel} {
        box-shadow: inset 0 0 0 1px
          ${(props) => props.theme.sem.color.action.tertiary.surface.stroke.default};
        background: ${(props) => props.theme.sem.color.action.tertiary.surface.fill.default};
        color: ${(props) => props.theme.sem.color.action.tertiary.foreground.text.default};

        &::before {
          background-color: ${(props) => props.theme.sem.color.action.tertiary.foreground.icon.default};
        }
      }

      ${StyledInput}:disabled + ${StyledLabel} {
        cursor: not-allowed;
        background-color: ${(props) => props.theme.sem.color.action.tertiary.surface.fill.disabled};
        color: ${(props) => props.theme.sem.color.action.tertiary.foreground.text.disabled};

        &::before {
          background-color: ${(props) => props.theme.sem.color.action.tertiary.foreground.icon.disabled};
        }
      }

      &:hover {
        ${StyledInput}:not(:disabled) + ${StyledLabel} {
          background: ${(props) => props.theme.sem.color.action.tertiary.surface.fill.hover};
          color: ${(props) => props.theme.sem.color.action.tertiary.foreground.text.hover};

          &::before {
            background-color: ${(props) => props.theme.sem.color.action.tertiary.foreground.icon.hover};
          }
        }
      }

      &:active {
        ${StyledInput}:not(:disabled) + ${StyledLabel} {
          background: ${(props) => props.theme.sem.color.action.tertiary.surface.fill.pressed};
          color: ${(props) => props.theme.sem.color.action.tertiary.foreground.text.pressed};

          &::before {
            background-color: ${(props) => props.theme.sem.color.action.tertiary.foreground.icon.pressed};
          }
        }
      }
    `}
`;
//# sourceMappingURL=index.styles.js.map